import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { purchaseRequestsService, offersService, dealsService } from '../../services/supabaseService';
import { trackEvent } from '../../hooks/useGoogleAnalytics';
import { realTimeMonitoringService } from '../../services/realTimeMonitoringService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import DealStatusIndicator from '../../components/ui/DealStatusIndicator';
import Select from '../../components/ui/Select';
import RequestCard from './components/RequestCard';
import MyOffersPanel from './components/MyOffersPanel';
import EarningsSummary from './components/EarningsSummary';
import SubmitOfferModal from './components/SubmitOfferModal';

const TravelerDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [filters, setFilters] = useState({
    destination: '',
    category: '',
    budgetRange: ''
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
      // Start real-time monitoring
      realTimeMonitoringService?.startMonitoring();
    }
    
    return () => {
      // Cleanup monitoring on unmount
      realTimeMonitoringService?.stopMonitoring();
    };
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [requestsData, offersData, dealsData] = await Promise.all([
        purchaseRequestsService?.getActiveRequests(),
        offersService?.getByTravelerId(user?.id),
        dealsService?.getByTravelerId(user?.id)
      ]);
      setRequests(requestsData || []);
      setOffers(offersData || []);
      setDeals(dealsData || []);
    } catch (error) {
      console.error('Load dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOffer = (requestId) => {
    const request = requests?.find(r => r?.id === requestId);
    if (request) {
      setSelectedRequest(request);
    }
  };

  const handleOfferSubmit = async (offerData) => {
    try {
      const newOffer = {
        requestId: selectedRequest?.id,
        travelerId: user?.id,
        proposedFee: offerData?.proposedFee,
        message: offerData?.message,
        status: 'pending'
      };

      const created = await offersService?.create(newOffer);
      if (created) {
        // Track offer submission event
        trackEvent('offer_submission', {
          request_id: selectedRequest?.id,
          traveler_id: user?.id,
          proposed_fee: offerData?.proposedFee,
          item_category: selectedRequest?.itemCategory,
          page: '/traveler-dashboard'
        });
        await loadDashboardData();
      }
    } catch (error) {
      console.error('Create offer error:', error);
    }
  };

  const handleViewDeal = async (offerId) => {
    try {
      const deal = deals?.find(d => d?.offerId === offerId);
      if (deal) {
        navigate('/deal-room-page', { state: { dealId: deal?.id } });
      }
    } catch (error) {
      console.error('View deal error:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const filteredRequests = requests?.filter((request) => {
    if (filters?.destination && request?.destinationCountry !== filters?.destination) return false;
    if (filters?.category && request?.category !== filters?.category) return false;
    if (filters?.budgetRange) {
      const budget = request?.budget;
      if (filters?.budgetRange === 'low' && budget >= 100000) return false;
      if (filters?.budgetRange === 'medium' && (budget < 100000 || budget >= 500000)) return false;
      if (filters?.budgetRange === 'high' && budget < 500000) return false;
    }
    return true;
  });

  const translations = {
    en: {
      title: 'Traveler Dashboard',
      availableRequests: 'Available Purchase Requests',
      filterByDestination: 'Filter by Destination',
      filterByCategory: 'Filter by Category',
      filterByBudget: 'Filter by Budget',
      allDestinations: 'All Destinations',
      allCategories: 'All Categories',
      allBudgets: 'All Budgets',
      lowBudget: 'Low (< 100K)',
      mediumBudget: 'Medium (100K - 500K)',
      highBudget: 'High (> 500K)',
      noRequests: 'No purchase requests available',
      checkBack: 'Check back later for new opportunities'
    },
    ko: {
      title: '여행자 대시보드',
      availableRequests: '이용 가능한 구매 요청',
      filterByDestination: '목적지별 필터',
      filterByCategory: '카테고리별 필터',
      filterByBudget: '예산별 필터',
      allDestinations: '모든 목적지',
      allCategories: '모든 카테고리',
      allBudgets: '모든 예산',
      lowBudget: '낮음 (< 100K)',
      mediumBudget: '중간 (100K - 500K)',
      highBudget: '높음 (> 500K)',
      noRequests: '이용 가능한 구매 요청 없음',
      checkBack: '나중에 새로운 기회를 확인하세요'
    }
  };

  const t = translations?.[currentLanguage];

  const destinationOptions = [
    { value: '', label: t?.allDestinations },
    { value: 'South Korea', label: 'South Korea' },
    { value: 'USA', label: 'USA' },
    { value: 'Japan', label: 'Japan' },
    { value: 'France', label: 'France' },
    { value: 'UK', label: 'UK' }
  ];

  const categoryOptions = [
    { value: '', label: t?.allCategories },
    { value: 'electronics', label: 'Electronics' },
    { value: 'fashion', label: 'Fashion' },
    { value: 'cosmetics', label: 'Cosmetics' },
    { value: 'food', label: 'Food' },
    { value: 'books', label: 'Books' },
    { value: 'sports', label: 'Sports' },
    { value: 'toys', label: 'Toys' },
    { value: 'health', label: 'Health' }
  ];

  const budgetOptions = [
    { value: '', label: t?.allBudgets },
    { value: 'low', label: t?.lowBudget },
    { value: 'medium', label: t?.mediumBudget },
    { value: 'high', label: t?.highBudget }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <DealStatusIndicator />
      
      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          <EarningsSummary deals={deals} currentLanguage={currentLanguage} />

          <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-4">
              {t?.availableRequests}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Select
                options={destinationOptions}
                value={filters?.destination}
                onChange={(value) => handleFilterChange('destination', value)}
                placeholder={t?.filterByDestination}
              />
              <Select
                options={categoryOptions}
                value={filters?.category}
                onChange={(value) => handleFilterChange('category', value)}
                placeholder={t?.filterByCategory}
              />
              <Select
                options={budgetOptions}
                value={filters?.budgetRange}
                onChange={(value) => handleFilterChange('budgetRange', value)}
                placeholder={t?.filterByBudget}
              />
            </div>

            {filteredRequests?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg text-muted-foreground">{t?.noRequests}</p>
                <p className="text-sm text-muted-foreground mt-2">{t?.checkBack}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRequests?.map((request) => (
                  <RequestCard
                    key={request?.id}
                    request={request}
                    onSubmitOffer={handleSubmitOffer}
                    currentLanguage={currentLanguage}
                  />
                ))}
              </div>
            )}
          </div>

          <MyOffersPanel
            offers={offers}
            onViewDeal={handleViewDeal}
            currentLanguage={currentLanguage}
          />
        </div>
      </main>

      <BottomTabNavigation />

      {selectedRequest && (
        <SubmitOfferModal
          request={selectedRequest}
          onSubmit={handleOfferSubmit}
          onClose={() => setSelectedRequest(null)}
          currentLanguage={currentLanguage}
        />
      )}
    </div>
  );
};

export default TravelerDashboard;